import type { LayerRect } from './types';

/** "Encaixar" fits the whole media inside the frame (contain, letter/pillarboxed);
 *  "Preencher" covers the frame edge to edge (cover, overflow cropped by the
 *  composition) — the b-roll shape. */
export type LayerFitMode = 'fit' | 'fill';

/** Re-lay a visual layer's rect over the whole composition, centered, in either
 *  fit or fill mode, preserving the media's aspect.
 *
 *  No media dimensions needed: rect fractions are relative to the composition,
 *  so a rect's displayed aspect is (w/h)·compAspect — and the media aspect is
 *  (w/h)·compAspect too, because `fitRectToAspect` establishes that at insert
 *  time and `applyCanvasResize`'s `lockAspect` preserves it. The compAspect
 *  factor cancels, leaving `w/h` as the invariant to hold.
 *
 *  A fill rect deliberately extends past the composition (w > 1 or h > 1); the
 *  frame crops it. `layerRectToStyle` must therefore not let a stylesheet clamp
 *  the media's width — see the `maxWidth: none` note there.
 *
 *  Degenerate rects (zero height, non-finite) are returned untouched. */
export function applyLayerFitMode(
  { rect, mode }: { rect: LayerRect; mode: LayerFitMode },
): LayerRect {
  const ratio = rect.w / rect.h;
  if (!Number.isFinite(ratio) || ratio <= 0) return rect;

  // The one rect edge that reaches the frame: the short side for fit, the long
  // side for fill. The other follows from the aspect and overflows (fill) or
  // falls short (fit).
  const w = mode === 'fit' ? Math.min(1, ratio) : Math.max(1, ratio);
  const h = w / ratio;

  return { x: (1 - w) / 2, y: (1 - h) / 2, w, h };
}
